import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiX, FiUsers, FiCalendar, FiLink, FiCheck, FiArrowRight, FiCopy, FiInfo } = FiIcons;

const GroupBuyModal = ({ isOpen, onClose, product, onConfirm }) => {
  const [goal, setGoal] = useState(10);
  const [description, setDescription] = useState('');
  const [deadline, setDeadline] = useState(new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]);
  const [isSuccess, setIsSuccess] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [copied, setCopied] = useState(false);

  const handleCreate = async () => {
    const groupBuy = await onConfirm(goal, deadline, description);
    setShareLink(`${window.location.origin}/#/product/${product.id}?gb=${groupBuy.id}`);
    setIsSuccess(true);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4 bg-accent-navy/60 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden max-h-[90vh] flex flex-col"
      >
        <div className="p-8 md:p-10 overflow-y-auto">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h2 className="text-2xl font-serif text-accent-navy mb-1">
                {isSuccess ? 'Group Buy Started' : 'Start a Group Buy'}
              </h2>
              <p className="text-[10px] text-text-muted font-black uppercase tracking-widest">
                {isSuccess ? 'Share the link to reach your goal' : `Collective purchase for ${product.name}`}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-brand-light rounded-full transition-colors">
              <SafeIcon icon={FiX} className="w-6 h-6 text-text-muted" />
            </button>
          </div>

          <AnimatePresence mode="wait">
            {!isSuccess ? (
              <motion.div 
                key="form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-8"
              >
                <div className="space-y-6">
                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3 block">Purchase Goal (Units)</label>
                    <div className="relative">
                      <SafeIcon icon={FiUsers} className="absolute left-4 top-1/2 -translate-y-1/2 text-accent-gold" />
                      <input
                        type="number"
                        min="2"
                        value={goal}
                        onChange={(e) => setGoal(parseInt(e.target.value))}
                        className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl text-accent-navy font-bold outline-none focus:border-accent-gold transition-colors"
                      />
                    </div>
                    <p className="text-[10px] text-text-muted mt-2">Minimum 2 participants required for group discount.</p>
                  </div>

                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3 block">Campaign Description</label>
                    <div className="relative">
                      <SafeIcon icon={FiInfo} className="absolute left-4 top-4 text-accent-gold" />
                      <textarea
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                        placeholder="Explain the rationale for this collective purchase..."
                        className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl text-accent-navy font-medium outline-none focus:border-accent-gold transition-colors min-h-[100px] resize-none"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-3 block">Campaign Deadline</label>
                    <div className="relative">
                      <SafeIcon icon={FiCalendar} className="absolute left-4 top-1/2 -translate-y-1/2 text-accent-gold" />
                      <input
                        type="date"
                        value={deadline}
                        min={new Date().toISOString().split('T')[0]}
                        onChange={(e) => setDeadline(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-brand-light border-2 border-brand-dark rounded-2xl text-accent-navy font-bold outline-none focus:border-accent-gold transition-colors"
                      />
                    </div>
                  </div>
                </div>

                <div className="bg-brand-light p-6 rounded-3xl border border-brand-dark">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center text-accent-gold shadow-sm">
                      <SafeIcon icon={FiCheck} />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-accent-navy">Special Group Pricing</h4>
                      <p className="text-[10px] text-text-muted">Reach the goal to unlock free shipping for all participants.</p>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleCreate}
                  className="w-full btn-primary py-5 text-[10px] font-black uppercase tracking-widest flex items-center justify-center gap-3 shadow-xl"
                >
                  Create Campaign <SafeIcon icon={FiArrowRight} />
                </button>
              </motion.div>
            ) : (
              <motion.div 
                key="success"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                className="space-y-8"
              >
                <div className="flex flex-col items-center justify-center text-center py-6">
                  <div className="w-20 h-20 bg-green-100 text-green-600 rounded-[32px] flex items-center justify-center mb-6 shadow-inner">
                    <SafeIcon icon={FiCheck} className="w-10 h-10" />
                  </div>
                  <h3 className="text-xl font-serif text-accent-navy mb-2">Campaign is Live!</h3>
                  <p className="text-sm text-text-muted font-light max-w-xs mx-auto">
                    Invite your community to join this collective purchase. The more people who join, the better the legacy.
                  </p>
                </div>

                <div className="space-y-4">
                  <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block">Shareable Link</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      readOnly
                      value={shareLink}
                      className="flex-grow bg-brand-light border-2 border-brand-dark rounded-xl px-4 py-3 text-sm font-medium text-text-muted outline-none"
                    />
                    <button
                      onClick={handleCopy}
                      className={`px-6 rounded-xl font-bold transition-all flex items-center gap-2 ${copied ? 'bg-green-500 text-white' : 'bg-accent-navy text-white hover:bg-accent-navy/90'}`}
                    >
                      <SafeIcon icon={copied ? FiCheck : FiCopy} />
                      {copied ? 'Copy' : 'Copy'}
                    </button>
                  </div>
                </div>

                <button
                  onClick={onClose}
                  className="w-full py-5 border-2 border-brand-dark rounded-3xl text-[10px] font-black uppercase tracking-widest text-accent-navy hover:bg-brand-light transition-all"
                >
                  Close & View Product
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default GroupBuyModal;